import { isDefined, isNullOrUndefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useUpdateFacilityProfile } from "@src/appV2/FacilityProfile/api/useUpdateFacilityProfile";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { ExternalLink } from "@src/appV2/lib/Links";
import { showSuccessToast } from "@src/appV2/lib/Notifications";
import { useDeprecatedDispatch } from "@src/appV2/lib/utils/useDeprecatedDispatch";
import { LoadingButton, Text, type UseModalState } from "@src/lib/ui-react";
import { addHours, startOfDay } from "date-fns";
import { Controller, useForm } from "react-hook-form";

import { PENDING_SETTINGS_HELP_LINK } from "../constants";
import { pendingSettingsSchema } from "../types";
import { convertDateToFacilityTimeZone } from "../utils";

export interface FormInputs {
  enabled: boolean;
  pendingDate?: Date;
  notify?: boolean;
}

interface EditFacilityPendedDateDialogProps {
  modalState: UseModalState;
  facilityId: string;
  facilityTmz: string;
  pendingSettings?: {
    enabled: boolean;
    pendingDate?: Date;
    notify?: boolean;
  };
  onClose: () => void;
}

export function EditFacilityPendingSettingsDialog(props: EditFacilityPendedDateDialogProps) {
  const { profile: deprecatedFacilityProfile } = useSession();
  const { deprecatedDispatch } = useDeprecatedDispatch();
  const { modalState, facilityId, facilityTmz, pendingSettings, onClose } = props;
  const { enabled, pendingDate } = pendingSettings ?? {};
  const { isLoading: isUpdateFacilityProfileLoading, mutateAsync: updateFacilityProfile } =
    useUpdateFacilityProfile();

  const handleResetNotifyAndClose = () => {
    // We need to reset notify to false before closing the dialog
    // because we want to start with no email notifications when the dialog is opened again
    // eslint-disable-next-line no-use-before-define
    reset({ enabled: enabled ?? false, notify: false, pendingDate });
    onClose();
  };

  const updatePendingSettings = async (params: FormInputs) => {
    const { enabled, pendingDate, notify } = params;
    const response = await updateFacilityProfile({
      facilityId,
      pendingSettings: {
        enabled,
        ...(isDefined(pendingDate) && {
          pendingDate,
        }),
        notify,
      },
    });
    if (isDefined(response?.pendingSettings)) {
      showSuccessToast("Updated pending settings successfully");
    }
  };

  const { control, handleSubmit, watch, reset, formState } = useForm({
    // Using default value as false for notify
    // because we want to start with no email notifications when the dialog is opened
    defaultValues: { enabled: enabled ?? false, pendingDate, notify: false },
    resolver: zodResolver(pendingSettingsSchema),
  });

  const { isValid, isDirty, errors } = formState;

  const pendingSettingsEnabled = watch("enabled");
  const { user } = useSession();
  return (
    <Dialog open={modalState.modalIsOpen} onClose={handleResetNotifyAndClose}>
      <DialogTitle>Add pending status date</DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSubmit(async (data) => {
            const { enabled, pendingDate, notify } = data;
            const updatePayload = {
              enabled: enabled ?? false,
              ...(enabled && {
                notify: notify ?? false,
              }),
              ...(enabled &&
                isDefined(pendingDate) && {
                  pendingDate: convertDateToFacilityTimeZone(pendingDate, facilityTmz),
                }),
            };
            try {
              await updatePendingSettings(updatePayload);
            } catch {
              // Error toast is shown by useUpdateFacilityProfile's onError handler
              // via extractErrorDetail. Abort the post-success side effects below
              // so we don't dispatch a Redux update / log / close on a failed save.
              return;
            }
            deprecatedDispatch({
              type: "session/UPDATE_PROFILE",
              data: {
                ...deprecatedFacilityProfile,
                pendingSettings: updatePayload,
              },
            });
            logEvent(APP_V2_USER_EVENTS.FACILITY_PENDING_NOTICE_ENABLED, {
              ...updatePayload,
              previousPendingSettings: pendingSettings,
              workplaceId: facilityId,
              employeeId: user?._id,
            });
            // We need to reset notify to false before closing the dialog
            // because we want to start with no email notifications when the dialog is opened again
            reset({ ...updatePayload, notify: false });
            onClose();
          })}
        >
          <Stack direction="column" spacing={2} alignItems="flex-start">
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(field.value)}
                      onChange={(event) => {
                        field.onChange(event.target.checked);
                      }}
                    />
                  }
                  label={
                    <Text variant="body1" sx={{ marginRight: 4 }}>
                      Pending Notice Enabled
                    </Text>
                  }
                  labelPlacement="start"
                />
              )}
            />
            <Controller
              name="notify"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      disabled={!pendingSettingsEnabled}
                      color="primary"
                      checked={Boolean(field.value)}
                      onChange={(event) => {
                        field.onChange(pendingSettingsEnabled && event.target.checked);
                      }}
                    />
                  }
                  label={
                    <Stack direction="column" sx={{ marginRight: 2, marginBottom: 2 }}>
                      <Text variant="body1">Send Pending Notice Email</Text>
                      <Text variant="caption">
                        <ExternalLink to={PENDING_SETTINGS_HELP_LINK}>How it works</ExternalLink>
                      </Text>
                    </Stack>
                  }
                  labelPlacement="start"
                />
              )}
            />
            <Controller
              name="pendingDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  disablePast
                  label="Date workplace will be pended"
                  disabled={!pendingSettingsEnabled}
                  value={field.value}
                  inputRef={field.ref}
                  slotProps={{
                    textField: {
                      error: Boolean(
                        pendingSettingsEnabled &&
                        (errors?.pendingDate ?? isNullOrUndefined(field.value))
                      ),
                      helperText:
                        pendingSettingsEnabled &&
                        (errors?.pendingDate ?? isNullOrUndefined(field.value)) &&
                        "Please enter a valid date.",
                    },
                  }}
                  onChange={(date) => {
                    // setting the date to afternoon 12 pm.
                    field.onChange(isDefined(date) ? addHours(startOfDay(date), 12) : date);
                  }}
                />
              )}
            />
            <Stack direction="row" spacing={1} alignItems="center" marginTop={2}>
              <Button onClick={handleResetNotifyAndClose}>Cancel</Button>
              <LoadingButton
                type="submit"
                variant="contained"
                isLoading={isUpdateFacilityProfileLoading}
                disabled={!isValid || !isDirty}
              >
                Submit
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
}
